import { inject, Injectable } from '@angular/core';
import { EnrollhereApiService } from '../../enrollhere-api/enrollhere-api';
import { AUTH } from '../../firebase/firebase.tokens';
import { CommonPermissionService } from '../common-permission.service';
import { ReportRequestCoordinator } from './report-request-coordinator';
import { ApiError } from '../../enrollhere-api/api.error';
import { ReportRequest, ReportResponse } from './reporting.types';
import { pushReportingDebug, reportingDebugEnabled } from './reporting-debug';

@Injectable({ providedIn: 'root' })
export class ReportingService {
  private readonly api = inject(EnrollhereApiService);

  private readonly auth = inject(AUTH);
  private readonly permissions = inject(CommonPermissionService);
  private readonly coordinator = new ReportRequestCoordinator<ReportResponse>(
    4
  );
  get pendingRequests(): number {
    return this.coordinator.pending;
  }
  scopeKey(): string {
    return JSON.stringify([
      this.auth.currentUser?.uid,
      this.permissions.selectedAgencyIds(),
      this.permissions.consolidated(),
    ]);
  }

  async report(
    request: ReportRequest,
    signal?: AbortSignal
  ): Promise<ReportResponse> {
    const scope = this.scopeKey();
    const snapshot = structuredClone(request);
    const response = await this.coordinator.run(
      JSON.stringify([scope, snapshot]),
      async transportSignal => {
        if (scope !== this.scopeKey() || transportSignal.aborted)
          throw new DOMException('Report scope changed', 'AbortError');
        return this.send(snapshot, transportSignal);
      },
      signal
    );
    if (scope !== this.scopeKey() || signal?.aborted)
      throw new DOMException('Report scope changed', 'AbortError');
    // Renderers may enrich rows; never share mutable responses between cards.
    return structuredClone(response);
  }

  private async send(
    request: ReportRequest,
    signal: AbortSignal
  ): Promise<ReportResponse> {
    if (!reportingDebugEnabled()) {
      return this.api.post<ReportResponse>(
        'reporting/report',
        request,
        undefined,
        signal
      );
    }
    try {
      return await this.api.post<ReportResponse>(
        'reporting/report',
        request,
        undefined,
        signal
      );
    } catch (error) {
      const status = error instanceof ApiError ? error.status : undefined;
      pushReportingDebug({
        kind: 'api',
        path: 'reporting/report',
        status,
        message: error instanceof Error ? error.message : String(error),
        request: summarizeReportRequest(request),
      });
      throw error;
    }
  }
}

function summarizeReportRequest(
  request: ReportRequest
): Record<string, unknown> {
  const firstGroup = request.groups?.[0];
  const firstDim = firstGroup?.dimensions?.[0];
  return {
    sources: request.sources?.map(s => s.index),
    group: firstGroup?.name,
    dimensionField: firstDim?.field,
    hasHistogram: Boolean(firstDim?.histogram),
    hasDateHistogram: Boolean(firstDim?.dateHistogram),
  };
}
